import { OptimizelyDecideOption, useDecision } from '@optimizely/react-sdk';

import { FeatureFlag } from './featureFlags';
import { DEFAULT_SPLIT_VARIATION, VariationTypes } from './variationTypes';

export interface FlagOptions {
  blockEvent?: boolean;
}

const TIMEOUT_MS = 1_000;

/**
 * React hook to retrieve the state of a feature flag.
 * Returns `null` if the Optimizely client is not ready and has not timed out (default timeout: 1s).
 * If the client does not respond within the timeout, the hook will return a fallback value based on the flag type.
 */
export const useFeatureFlag = (
  featureFlag: { variationType: typeof VariationTypes.ROLLOUT } & FeatureFlag,
  options?: FlagOptions,
) => {
  const [decision, clientReady, didTimeout] = useDecision(featureFlag.name, {
    autoUpdate: true,
    decideOptions: options?.blockEvent ? [OptimizelyDecideOption.DISABLE_DECISION_EVENT] : [],
    timeout: TIMEOUT_MS,
  });

  if (!clientReady && !didTimeout) {
    return null;
  }

  if (featureFlag.variationType === VariationTypes.ROLLOUT) {
    return { enabled: decision.variationKey === 'on' };
  }

  const variation =
    decision.variationKey === 'off' ? DEFAULT_SPLIT_VARIATION : decision.variationKey;

  return { variation };
};
